import { useEffect } from 'react';

import connectionAPIGetLinkedConnections from '@furality/boop-client/functions/connectionAPIGetLinkedConnections.js';
import connectionAPILinkConnection from '@furality/boop-client/functions/connectionAPILinkConnection.js';
import { AuthState, ReportError, useAuth, useMutation, useQuery } from '@furality/frontend-stack';

export const REDIRECT_PARAM = 'discordRedirect' as const;
const CONNECTION_TYPE = 'discord';
const RETURN_PATH_KEY = 'discordRedirectPath';

const DISCORD = Symbol('discord');

export class LinkFailure extends Error {}

type DiscordUser = {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl?: string;
};

const DiscordStatus = {
  Loading: 'loading',
  Error: 'error',
  NotLinked: 'notLinked',
  Linked: 'linked',
} as const;
type DiscordStatus = (typeof DiscordStatus)[keyof typeof DiscordStatus];

const redirectQueryParam = `${REDIRECT_PARAM}=true`;

export const useDiscordRedirect = () => {
  useEffect(() => {
    if (!window.location.search.includes(redirectQueryParam)) {
      return;
    }

    const returnPath = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);

    if (returnPath != null && returnPath !== window.location.pathname) {
      window.location.replace(`${returnPath}?${redirectQueryParam}`);
    }
  }, []);
};

const useDiscord = (boopBaseUrl: string) => {
  const auth = useAuth();
  useDiscordRedirect();

  const query = useQuery({
    queryKey: [DISCORD],
    queryFn: async () => {
      const r = await connectionAPIGetLinkedConnections({
        config: {
          basePath: boopBaseUrl,
          auth: {
            bearer: auth.auth?.token,
          },
        },
      });

      if (r.status !== 200) {
        throw new ReportError(r);
      }

      return r.data.connections.find((c) => c.type === CONNECTION_TYPE) ?? null;
    },

    enabled: auth.state === AuthState.LoggedIn,
  });

  const link = useMutation({
    mutationFn: async () => {
      sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname);
      const redirectUri = `${window.location.origin}/?${redirectQueryParam}`;
      const response = await connectionAPILinkConnection({
        config: {
          basePath: boopBaseUrl,
          auth: {
            bearer: auth.auth?.token,
          },
        },
        params: {
          connection: CONNECTION_TYPE,
          redirect_uri: redirectUri,
        },
      });

      if (response.status !== 200) {
        throw new ReportError(response);
      }

      window.location.href = response.data.redirectUrl;
    },
  });

  const previousLinkFailed =
    query.isSuccess && query.data == null && window.location.search.includes(redirectQueryParam);

  if (previousLinkFailed) {
    return {
      status: DiscordStatus.Error,
      error: new LinkFailure(),
      query,
      link,
    };
  }

  if (query.isError) {
    return {
      status: DiscordStatus.Error,
      error: query.error,
      query,
      link,
    };
  }

  if (link.isError) {
    return {
      status: DiscordStatus.Error,
      error: link.error,
      query,
      link,
    };
  }

  if (query.isFetching || link.isPending) {
    return {
      status: DiscordStatus.Loading,
      query,
      link,
    };
  }

  if (query.data == null) {
    return {
      status: DiscordStatus.NotLinked,
      query: query,
      link,
    };
  }

  const user: DiscordUser = {
    id: query.data.remoteUserId,
    handle: query.data.remoteUsername ?? '<unknown>',
    displayName: query.data.remoteDisplayName,
    avatarUrl: query.data.remoteAvatarUrl ?? undefined,
  };

  return {
    status: DiscordStatus.Linked,
    user,
    query,
    link,
  };
};

export default useDiscord;
